/**
 * Utility for managing shop ID in development/testing
 * In production, this would come from Shopify App Bridge session
 */

export function getShopId(): string | undefined {
  // Test/dev shop fallbacks must never ship in production builds.
  // In prod the shop identity comes from the App Bridge session token.
  if (import.meta.env.DEV) {
    // Try to get from environment variable (for testing)
    const envShopId = import.meta.env.VITE_TEST_SHOP_ID;
    if (envShopId) {
      return envShopId;
    }

    // Try to get from localStorage (for development)
    if (typeof window !== 'undefined') {
      const storedShopId = localStorage.getItem('test_shop_id');
      if (storedShopId) {
        return storedShopId;
      }
    }
  }

  // In production, shop identity comes from Shopify App Bridge auth
  return undefined;
}

export function setShopId(shopId: string): void {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    localStorage.setItem('test_shop_id', shopId);
  }
}

export function clearShopId(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('test_shop_id');
  }
}

/**
 * Get shop domain from URL params (shop or host query param)
 * Returns the extracted shop domain or null if not found
 */
export function getShopDomain(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const shopDomain = urlParams.get('shop') || urlParams.get('host');
  
  if (!shopDomain) {
    return null;
  }

  // Extract shop domain from host if it's base64 encoded
  try {
    // Host is base64 encoded: YWRtaW4uc2hvcGlmeS5jb20vc3RvcmUvYXItc3BhY2UtcGxhdGZvcm0
    // Decode and extract shop domain
    const decoded = atob(shopDomain);
    // Format: admin.shopify.com/store/shop-name
    const match = decoded.match(/store\/([^\/]+)/);
    if (match) {
      return `${match[1]}.myshopify.com`;
    }
  } catch (e) {
    // Not base64, use as is if it contains .myshopify.com
    if (shopDomain.includes('.myshopify.com')) {
      return shopDomain;
    }
  }

  // Return as is if it looks like a shop domain
  if (shopDomain.includes('myshopify.com')) {
    return shopDomain;
  }

  return null;
}

/**
 * Check if shop domain is available (either in URL params or via shop ID)
 * Returns true if shop domain can be determined, false otherwise
 */
export function hasShopContext(): boolean {
  const shopDomain = getShopDomain();
  const shopId = getShopId();
  
  return !!(shopDomain || shopId);
}

